import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { AuthGuard } from 'src/auth/guard';
import PortalLayout from 'src/layouts/portal';

import { LoadingScreen } from 'src/components/loading-screen';

// ----------------------------------------------------------------------

const PortalHomePage = lazy(() => import('src/pages/portal/home'));
const PortalPackagesPage = lazy(() => import('src/pages/portal/packages'));
const PortalLandingPage = lazy(() => import('src/pages/portal/landing'));
const PortalRedirectPage = lazy(() => import('src/pages/portal/redirect'));
const PortalShipmentsPage = lazy(() => import('src/pages/portal/shipments'));
const PortalShipmentDetailPage = lazy(() => import('src/pages/portal/shipment-detail'));
const PortalPassportsPage = lazy(() => import('src/pages/portal/passports'));
const PortalPassportDetailPage = lazy(() => import('src/pages/portal/passport-detail'));
const PortalRemittancesPage = lazy(() => import('src/pages/portal/remittances'));
const PortalRemittanceDetailPage = lazy(() => import('src/pages/portal/remittance-detail'));
const PortalMigrationsPage = lazy(() => import('src/pages/portal/migrations'));
const PortalMigrationDetailPage = lazy(() => import('src/pages/portal/migration-detail'));

// ----------------------------------------------------------------------

export const portalRoutes = [
  {
    path: 'portal',
    element: (
      <AuthGuard>
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </AuthGuard>
    ),
    children: [
      // Bare /portal — smart redirector / agency chooser, rendered without the locker shell
      // since the user hasn't entered a specific agency's locker yet.
      { index: true, element: <PortalRedirectPage /> },
      {
        element: (
          <PortalLayout>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </PortalLayout>
        ),
        children: [
          { path: 'home', element: <PortalHomePage /> },
          { path: 'packages', element: <PortalPackagesPage /> },
          { path: 'shipments', element: <PortalShipmentsPage /> },
          { path: 'shipments/:id', element: <PortalShipmentDetailPage /> },
          { path: 'passports', element: <PortalPassportsPage /> },
          { path: 'passports/:id', element: <PortalPassportDetailPage /> },
          { path: 'remittances', element: <PortalRemittancesPage /> },
          { path: 'remittances/:id', element: <PortalRemittanceDetailPage /> },
          { path: 'migrations', element: <PortalMigrationsPage /> },
          { path: 'migrations/:id', element: <PortalMigrationDetailPage /> },
        ],
      },
    ],
  },
  // Anonymous, branded per-agency landing. The static children above rank higher
  // than this dynamic segment, so /portal/home still resolves to the authed portal.
  {
    path: 'portal/:agencySlug',
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <PortalLandingPage />
      </Suspense>
    ),
  },
];
